import { isRole } from "PFApp/activities/helpers";
import {
  ACTIVITY_TAG_ID,
  ACTIVITY_TAG_NOTE_ID
} from "PFApp/activities/show/activity_page/parts/tag/use_manage_tags_permissions";
import { useTemplate } from "PFApp/hooks";
import { Stack } from "PFComponents/containers";
import { LoadingDots } from "PFComponents/loading_dots";
import { Modal } from "PFComponents/modal";
import { SearchableVariant, SelectV2 } from "PFComponents/select_v2";
import { InputFieldSet } from "PFComponents/text/input_field_set";
import Toggle from "PFComponents/toggle/toggle";
import { Typography } from "PFComponents/typography";
import { getActivityCustomFieldValues } from "PFCore/helpers/activity_custom_fields";
import { getProfileName } from "PFCore/helpers/profile";
import { useCustomTypes } from "PFCore/helpers/use_custom_types";
import { useActivity } from "PFCore/hooks/queries/activity";
import { useCustomValues } from "PFCore/hooks/queries/custom_values/use_custom_values";
import { useCurrentProfile } from "PFCore/hooks/queries/profile/use_current_profile";
import { TagPayload } from "PFCore/services/activities/tags";
import { Id } from "PFCore/types";
import { Option, OptionOriginal } from "PFTypes/options";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  extractRecipientUsers,
  ManageTagsModalAction,
  mapTagToOption,
  RecipientUser
} from "./manage_tags_modal.utils";
import { RecipientOption } from "./parts/recipient_option";
import { useManageTagsSubmit } from "./use_manage_tags_submit";

const NOTE_LENGTH_MAX = 250;

type ManageTagsModalProps = {
  activityId: Id;
  templateId: number;
  onClose: () => void;
  onSuccess?: () => void;
  onError?: () => void;
};

export const ManageTagsModal = ({
  activityId,
  templateId,
  onClose,
  onSuccess,
  onError
}: ManageTagsModalProps) => {
  const { t } = useTranslation("activities");
  const { getCustomTypeByName } = useCustomTypes();
  const { data: activity } = useActivity(activityId);
  const { data: currentProfile } = useCurrentProfile();
  const template = useTemplate({ id: templateId });

  const tagCustomType = getCustomTypeByName(ACTIVITY_TAG_ID);
  const { data: tagCustomValues } = useCustomValues(
    { customTypeId: tagCustomType?.id },
    { enabled: !!tagCustomType }
  );

  const tagOptions = tagCustomValues?.entries?.map(mapTagToOption) ?? [];

  const { handleSubmit } = useManageTagsSubmit({ activity, onSuccess, onError });

  const prefilledTagId = getActivityCustomFieldValues(activity, ACTIVITY_TAG_ID)?.[0]?.id;
  const prefilledNote = activity?.metadata[ACTIVITY_TAG_NOTE_ID];

  const defaultAction = prefilledTagId ? ManageTagsModalAction.Update : ManageTagsModalAction.Create;

  const [action, setAction] = useState<ManageTagsModalAction>(defaultAction);
  const [selectedTag, setSelectedTag] = useState<Id | undefined>(prefilledTagId);
  const [note, setNote] = useState(prefilledNote ?? "");
  const [isNotifyOthersEnabled, setIsNotifyOthersEnabled] = useState(false);
  const [selectedRecipientIds, setSelectedRecipientIds] = useState<Id[]>([]);

  useEffect(() => {
    setSelectedTag(prefilledTagId);
    setNote(prefilledNote ?? "");
  }, [prefilledTagId, prefilledNote]);

  const recipientOptions = useMemo(() => {
    const recipients = extractRecipientUsers(activity);

    return recipients.map((recipient) => {
      const fullName = getProfileName(recipient);
      return {
        id: recipient.id,
        value: fullName,
        displayElement: <RecipientOption recipient={recipient} />,
        disabled: recipient.id === currentProfile.id,
        original: recipient
      };
    });
  }, [activity, currentProfile.id]);

  const handleChange = (options) => {
    setSelectedTag(options.id);
  };

  const handleNotifyOthersChange = useCallback((value: boolean) => {
    setIsNotifyOthersEnabled(value);

    if (!value) {
      setSelectedRecipientIds([]);
    }
  }, []);

  const handleRecipientsChange = useCallback((options: Option<RecipientUser>[]) => {
    setSelectedRecipientIds(options.map((option) => option.id));
  }, []);

  const getLabelOk = () => {
    if (action === ManageTagsModalAction.Create) {
      return t("show.tags.addTag");
    }
    return t("show.tags.updateTag");
  };

  const getDescription = () => {
    if (action === ManageTagsModalAction.Create) {
      if (!template) {
        return null;
      }
      return isRole(template) ? t("show.tags.descriptionRoleAdd") : t("show.tags.descriptionEngagementAdd");
    }
    return t("show.tags.descriptionUpdate");
  };

  useEffect(() => {
    if (prefilledTagId !== selectedTag) {
      setNote("");
    } else {
      setNote(prefilledNote ?? "");
    }
  }, [selectedTag, prefilledTagId, prefilledNote]);

  useEffect(() => {
    const isTagChanged = selectedTag !== prefilledTagId;
    const isNoteChanged = prefilledNote !== note;

    if (!prefilledTagId) {
      setAction(ManageTagsModalAction.Create);
    } else if (!!prefilledTagId && !!selectedTag && (isTagChanged || isNoteChanged)) {
      setAction(ManageTagsModalAction.Update);
    } else if (!!prefilledTagId && !selectedTag) {
      setAction(ManageTagsModalAction.Delete);
    }
  }, [selectedTag, note, prefilledTagId, prefilledNote]);

  const handleOk = () => {
    const payload: TagPayload = {
      customValueId: selectedTag,
      note
    };

    if (isNotifyOthersEnabled && selectedRecipientIds.length > 0) {
      payload.notificationRecipientIds = selectedRecipientIds;
    }

    handleSubmit(action, payload);
  };

  const isOkDisabled = () => {
    if (isNotifyOthersEnabled && selectedRecipientIds.length === 0) {
      return true;
    }

    switch (action) {
      case ManageTagsModalAction.Create:
        return !selectedTag;
      case ManageTagsModalAction.Update:
        return prefilledTagId === selectedTag && prefilledNote === note;
      case ManageTagsModalAction.Delete:
        return prefilledTagId === selectedTag;
    }
  };

  const renderDisplayValue = (value: Option<RecipientUser>[]) => {
    if (!value || value.length === 0) {
      return "";
    }

    return t("show.tags.notificationDisplayLabel", { count: value.length });
  };

  return (
    <Modal
      title={t("show.tags.title")}
      subtitle={activity ? activity.name : <LoadingDots />}
      labelOK={getLabelOk()}
      onOK={handleOk}
      disableOKButton={isOkDisabled()}
      onClose={onClose}
    >
      <Stack>
        <Typography variant="bodyRegular">{getDescription()}</Typography>
        <SelectV2<OptionOriginal>
          label={t("show.tags.tag")}
          value={selectedTag}
          options={tagOptions}
          onChange={handleChange}
          multiple={false}
          onClear={selectedTag ? () => setSelectedTag(undefined) : undefined}
          searchableVariant={SearchableVariant.Always}
        />
        <InputFieldSet
          label={t("show.tags.note")}
          value={note}
          inputType="textarea"
          maxLength={NOTE_LENGTH_MAX}
          minHeight={100}
          minVisibleMaxLengthFactor={0.9}
          qaId={`manage-tags-note`}
          onChange={setNote}
          disabled={action === ManageTagsModalAction.Delete}
        />
        <Toggle
          label={t("show.tags.notifyOthers")}
          inline
          reversed
          checked={isNotifyOthersEnabled}
          onChange={handleNotifyOthersChange}
          qaId="notify-others-toggle"
        />
        {isNotifyOthersEnabled && (
          <SelectV2<RecipientUser>
            label={t("show.tags.notificationLabel")}
            value={selectedRecipientIds}
            renderDisplayValue={renderDisplayValue}
            options={recipientOptions}
            onChange={handleRecipientsChange}
            multiple
            searchableVariant={SearchableVariant.Always}
            qaId="recipients-select"
            tip={t("show.tags.notificationInfo")}
          />
        )}
      </Stack>
    </Modal>
  );
};
